<template id="media-preview">
  <div class="outer-container">
    <div class="media-thumbnail-container" v-if="computedThumbnail != null">
      <AuthenticatedMedia cssClass="media-thumbnail" :mediaUrl="computedThumbnail" type="image"></AuthenticatedMedia>
      <ion-icon class="play-overlay" draggable="false" v-if="type != null && type.includes('video')" :icon="play" color="primary"></ion-icon>
    </div>
    <div class="media-thumbnail-container" v-else-if="type != null && type.includes('image')">
      <AuthenticatedMedia cssClass="media-thumbnail" :mediaUrl="mediaUrl" type="image"></AuthenticatedMedia>
      <AuthenticatedMedia v-if="imposedImageUrl != null" cssClass="imposed-image-thumbnail" :style="imposedStyles" :mediaUrl="imposedImageUrl" type="image"></AuthenticatedMedia>
    </div>
    <div class="media-thumbnail video-placeholder" v-else-if="type != null && type.includes('video')">
      <ion-icon draggable="false" :icon="film" color="primary"></ion-icon>
    </div>
    <div class="media-thumbnail file-placeholder" v-else>
      <ion-icon draggable="false" :icon="document" color="primary"></ion-icon>
    </div>
  </div>
</template>

<script>
import { IonIcon } from '@ionic/vue';
import { defineComponent, computed, ref, watch } from 'vue';

import { getThumbnailFromBlobURL } from '@/utils/media';

import AuthenticatedMedia from '@/components/AuthenticatedMedia.vue';

import { useStore } from 'vuex';

import { film, play, document} from 'ionicons/icons';

export default defineComponent({
  name: 'MediaPreview',
  components: { IonIcon, AuthenticatedMedia },
  props: {
    'type': String,
    'mediaUrl': String,
    'thumbnailUrl': String,
    'loadAutomaticThumbnail': {
      type: Boolean,
      default: true
    },
    'imposedImageUrl': String,
    'imposedOpacity': {
      type: Number,
      default: 0.6
    },
    'imposedParameters': Object
  },
  setup(props) {
    const store = useStore();

    const imposedStyles = computed(() => {
      let style = `opacity: ${props.imposedOpacity};`
      if (props.imposedParameters != null) {
        if (props.imposedParameters.origin != null) style += `object-position: ${props.imposedParameters.origin};`
      }
      return style;
    });

    const loadedThumbnailPromise = ref(null);
    
    const loadedThumbnail = ref(null);

    //Once a promise is set, wait for it and set it
    watch(loadedThumbnailPromise, (thumbnailPromise) => {
      if (thumbnailPromise != null) thumbnailPromise.then((thumbnail) => loadedThumbnail.value = thumbnail);
    }, { immediate: true })

    //Returns either the given thumbnail or the automatically loaded one, whichever exists
    const computedThumbnail = computed(() => {
      if (props.thumbnailUrl != null) return props.thumbnailUrl;
      if (loadedThumbnail.value != null) return loadedThumbnail.value;
      return undefined;
    });

    const loadedThroughRelay = computed(() => store.getters['auth/isAuthenticationRelayActive']);

    watch([() => props.mediaUrl, () => props.type, () => props.thumbnailUrl, () => props.loadAutomaticThumbnail, loadedThroughRelay], ([currentUrl, mime, existingThumbnail, loadAutomatic, throughRelay]) => {
      //Only load it once, use promise to also not load it concurrently multiple times
      if (loadedThumbnailPromise.value == null) {
        //If not already a thumbnail exists and automatic loading is enabled, load it
        if (existingThumbnail == null && loadAutomatic && currentUrl != null && mime != null) {
          let filePromise;
          if (throughRelay) { //Through relay, take media URL directly by returning it through async function
            filePromise = (async () => ({ url: currentUrl, needsRevoking: false }))();
          } else { //Load it manually - no relay available
            filePromise = store.dispatch('fetchMediaWithAuthentication', {mediaURL: currentUrl});
          }

          loadedThumbnailPromise.value = filePromise.then(async ({ url, needsRevoking } = {}) => {
            if (url != null) {
              let thumbnail = await getThumbnailFromBlobURL(url, mime);
              if (needsRevoking) URL.revokeObjectURL(url);
              return thumbnail; //TODO Revoke thumbnail when not needed anymore
            }
          })
          .catch((error) => {
            console.error('Error loading thumbnail with authentication', error);
            return undefined;
          });
        }
      }
    }, { immediate: true });

    return { imposedStyles, film, play, document, computedThumbnail }
  },
})
</script>

<style scoped>
.video-placeholder, .file-placeholder { /* TODO Check height again when viewing reports! */
  position: relative;
  min-width: 100px;
  max-width: 100%;
  min-height: 100%;
  height: 100%;
}

.media-thumbnail, :deep(.media-thumbnail) {
  cursor: pointer;
  position: relative;
  height: auto;
  width: auto;
  border: var(--outer-border-width, 2px) solid var(--border-color, transparent);
  border-radius: var(--border-radius, 6px);
  /* max-height has to be explicit, because height is never set explicitly on container! */
  max-height: var(--height, 4em);
  max-width: 100%;
}

:deep(.imposed-image-thumbnail) {
  position: absolute;
  z-index: 1;
  left: 0;
  top: 0;
  width: 100%!important;
  height: 100%!important;
  border: var(--outer-border-width, 2px) solid transparent;
  border-radius: var(--border-radius, 6px);
  object-fit: contain;
  object-position: center;
}

.outer-container {
  position: relative;
  display: block;
  height: 100%;
  max-height: 100%;
  line-height: 0px; /* Line-height fixes alignment issue on scaled down images, because inline elements rely on baseline for sizing, and if image is smaller than line-height it is not aligned anymore */
  height: var(--height, 4em);
}

.media-thumbnail-container {
  cursor: pointer;
  position: relative;
  display: block;
  max-height: 100%;
  height: auto;
}

.play-overlay, .video-placeholder ion-icon, .file-placeholder ion-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: 2em;
  max-width: 2em;
  height: calc(75% - (var(--outer-border-width, 2px) * 2));
  width: calc(75% - (var(--outer-border-width, 2px) * 2));
  opacity: 0.9;
}

.video-placeholder ion-icon, .file-placeholder ion-icon {
  opacity: 1!important;
  min-height: 90%;
  min-width: 90%;
}
</style>